/**
 * Normalize and validate phone number
 * Supports US numbers and international format
 */
export const normalizeAndValidatePhone = (phone: string): { 
  isValid: boolean; 
  normalizedPhone: string; 
  error?: string 
} => {
  // Remove all spaces, dashes, parentheses and dots
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // If phone number is empty
  if (!cleanPhone) {
    return { isValid: false, normalizedPhone: '', error: 'Phone number is required' };
  }
  
  // Case 1: Number starts with + but without US country code (e.g. +6504415356)
  if (/^\+[2-9]\d{9}$/.test(cleanPhone)) {
    const fixedPhone = '+1' + cleanPhone.substring(1);
    return { isValid: true, normalizedPhone: fixedPhone };
  }
  
  // Case 2: Regular US number without + (e.g. 6504415356 or 16504415356)
  if (/^\d{10}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: '+1' + cleanPhone };
  }
  
  if (/^1\d{10}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: '+' + cleanPhone };
  }
  
  // Case 3: Already correct international format
  if (/^\+[1-9]\d{6,14}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: cleanPhone };
  }
  
  // Case 4: Number starts with + but is incorrect
  if (cleanPhone.startsWith('+')) {
    return { 
      isValid: false, 
      normalizedPhone: cleanPhone,
      error: 'Invalid international phone format. For US numbers use format like +16504415356'
    };
  }
  
  // Other cases
  return { 
    isValid: false, 
    normalizedPhone: cleanPhone,
    error: 'Phone number must be 10 digits (US) or international format (+country code + number)'
  };
};

/**
 * Format phone number for display
 * +16504415356 -> +1 (650) 441-5356
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '';
  
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // US format: +1XXXXXXXXXX
  if (/^\+1\d{10}$/.test(cleanPhone)) {
    const areaCode = cleanPhone.substring(2, 5);
    const firstPart = cleanPhone.substring(5, 8);
    const secondPart = cleanPhone.substring(8, 12);
    return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
  }
  
  // Return as is for other formats
  return phone;
};

